import React, { useState, useContext, useEffect, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { HttpPortProtocol } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { Button } from "components/Graphs/Button";
import UIButton from "components/UI-lib/Button";
import { OrgContext } from "contexts/OrgContext";
import { RemoveNodeDialog } from "../../Environment/RegisteredCompute/RemoveNodeDialog";
import { useNodeDetail } from "./useNodeDetail";
import Spinner from "components/UI-lib/Spinner";
import {
  CopyButton,
  EnableSSHBanner,
  productNameToDisplayName,
} from "./shared";
import CliCommands from "components/NodeDetail/shared/CliCommands";
import { NodeSecureLinksSection } from "./NodeSecureLinksSection";
import { NodePortsSection } from "./NodePortsSection";
import { NodeSSHAccessSection } from "./NodeSSHAccessSection";
import { bytesToGBString } from "../../Environment/RegisteredCompute/utils";
import UsernameDisplayField from "components/UsernameDisplayField";
import { isOrgAdminOrMaintainerRole, isOrgAdminRole } from "utils/roleUtils";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import ArchitectureBadge from "components/Environment/shared/ArchitectureBadge";
import { NetworkModeSummary } from "components/Environment/shared/NetworkModeSummary";
import { NetworkAddressCard } from "components/NodeDetail/shared/NetworkAddressCard";
import { resolveNetworkMemberType } from "components/Environment/Networks/networkMode";
import { DetailBreadcrumb } from "components/NodeDetail/shared/DetailBreadcrumb";
import {
  shouldScrollToSshAccess,
  type SshAccessNavigationState,
} from "components/Environment/shared/sshAccessNavigation";
import { NodeDetailSection } from "components/NodeDetail/shared/NodeDetailSection";

interface ParamTypes {
  nodeId: string;
  orgId: string;
}
const formatDate = (ts?: { seconds: bigint }) =>
  ts
    ? new Date(Number(ts.seconds) * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";
const isHttpPort = (port: DisplayPort) =>
  port.httpProtocol !== HttpPortProtocol.UNSPECIFIED;

const NodeDetail: React.FC = () => {
  const { nodeId, orgId } = useParams<ParamTypes>();
  const history = useHistory();
  const location = useLocation<SshAccessNavigationState>();
  const orgContext = useContext(OrgContext);
  const isAdmin = isOrgAdminRole(orgContext.activeOrgRole);
  const canChangeNetworkMode = isOrgAdminOrMaintainerRole(
    orgContext.activeOrgRole,
  );
  const {
    node,
    isLoading,
    openPort,
    openHTTPPort,
    setPortTarget,
    setPortAllowedSources,
    setHTTPPortAccess,
    closePort,
    grantSSHAccess,
    revokeSSHAccess,
    removeNode,
    setNetworkMemberType,
  } = useNodeDetail(nodeId);
  const [removeNodeDialogOpen, setRemoveNodeDialogOpen] = useState(false);
  const sshAccessSectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    document.title = node?.name
      ? `${node.name} | Brev.dev`
      : "Node Details | Brev.dev";
  }, [node]);
  const nodeLoaded = !isLoading && Boolean(node);
  useEffect(() => {
    if (nodeLoaded && shouldScrollToSshAccess(location.hash, location.state)) {
      sshAccessSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [location.hash, location.state, nodeLoaded]);
  if (isLoading)
    return (
      <div className="h-screen flex justify-center items-center">
        <Spinner type="secondary" />
      </div>
    );
  if (!node)
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        <p className="text-sm text-gray-500 dark:text-secondary">
          Node not found.
        </p>
        <Button
          variant="secondary"
          onClick={() => history.push(`/org/${orgId}/environments`)}
        >
          Back to Environments
        </Button>
      </div>
    );
  const gpus = node.nodeSpec?.gpus ?? [];
  const nodeProductName = productNameToDisplayName(node.nodeSpec?.productName);
  const allPorts: DisplayPort[] = node.ports ?? [];
  const httpPorts = allPorts.filter(isHttpPort);
  const tcpPorts = allPorts.filter((port) => !isHttpPort(port));
  const sshAccess = node.sshAccess;
  const breadcrumbItems = [
    {
      label: orgContext.activeOrg?.name,
      onNavigate: () => history.push(`/org/${orgId}/environments`),
    },
    { label: node.name || node.externalNodeId },
  ];
  return (
    <>
      <div className="min-h-full">
        <header className="pt-8 pb-3 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
            <div className="flex-1 min-w-0 pb-3">
              <DetailBreadcrumb items={breadcrumbItems} />
              <div className="flex flex-row justify-between items-end">
                <div className="flex flex-row items-center gap-3 mt-8">
                  <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-gray-100 sm:text-2xl sm:tracking-tight sm:truncate">
                    {node.name || node.externalNodeId}
                  </h1>
                </div>
              </div>

              {/* Creator, node id, added date */}
              <div className="flex flex-row items-center mt-2 gap-2">
                {node.createdBy && (
                  <UsernameDisplayField
                    userId={node.createdBy}
                    className="text-xs text-secondary"
                  />
                )}
                {node.createdBy && (
                  <span className="text-xs text-secondary">&middot;</span>
                )}
                <span className="flex items-center gap-0.5">
                  <span className="text-xs font-mono text-secondary">
                    {node.externalNodeId}
                  </span>
                  <CopyButton value={node.externalNodeId} />
                </span>
                {node.createTime && (
                  <>
                    <span className="text-xs text-secondary">&middot;</span>
                    <span className="text-xs text-secondary">
                      Added {formatDate(node.createTime)}
                    </span>
                  </>
                )}
              </div>

              {/* Node specs */}
              <div className="flex flex-col sm:flex-row justify-between text-secondary">
                <div className="flex flex-row flex-wrap items-center pt-2 gap-y-1 divide-x divide-neutral-800">
                  {[
                    nodeProductName ? (
                      <span
                        key="product"
                        className="text-sm font-medium text-highlight px-2 first:pl-0"
                      >
                        NVIDIA {nodeProductName}
                      </span>
                    ) : null,
                    ...gpus.map((gpu, i) => (
                      <span
                        key={`gpu-${i}`}
                        className="text-sm px-3 first:pl-0"
                      >
                        {gpu.count > 1 ? `${gpu.count}× ` : ""}
                        {gpu.model}
                        {gpu.memoryBytes
                          ? ` · ${bytesToGBString(gpu.memoryBytes)} VRAM`
                          : ""}
                      </span>
                    )),
                    node.nodeSpec?.cpuCount != null ? (
                      <span key="cpu" className="text-sm px-3 first:pl-0">
                        {node.nodeSpec.cpuCount} CPUs
                      </span>
                    ) : null,
                    node.nodeSpec?.ramBytes != null ? (
                      <span key="ram" className="text-sm px-3 first:pl-0">
                        {bytesToGBString(node.nodeSpec.ramBytes)} RAM
                      </span>
                    ) : null,
                    node.nodeSpec?.architecture ? (
                      <span key="architecture" className="px-3 first:pl-0">
                        <ArchitectureBadge
                          architectures={node.nodeSpec.architecture}
                          compact
                        />
                      </span>
                    ) : null,
                    (node.nodeSpec?.storage?.length ?? 0) > 0 ? (
                      <span key="storage" className="text-sm px-3 first:pl-0">
                        {bytesToGBString(
                          node.nodeSpec!.storage.reduce(
                            (sum, storage) => sum + storage.storageBytes,
                            BigInt(0),
                          ),
                        )}{" "}
                        Storage
                      </span>
                    ) : null,
                  ].filter(Boolean)}
                </div>

                {/* Remove node button */}
                {isAdmin && (
                  <div className="flex flex-col xl:mt-0">
                    <UIButton
                      label="Remove"
                      type="danger"
                      onClick={() => setRemoveNodeDialogOpen(true)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        <main className="pb-16 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <NodeDetailSection title="HTTP Ports" className="mt-8">
              <NodeSecureLinksSection
                ports={httpPorts}
                onOpenHTTPPort={openHTTPPort}
                onSetPortTarget={setPortTarget}
                onSetPortAllowedSources={setPortAllowedSources}
                onSetHTTPPortAccess={setHTTPPortAccess}
                onClosePort={closePort}
              />
            </NodeDetailSection>
            <NodeDetailSection title="TCP/UDP Ports">
              <NodePortsSection
                ports={tcpPorts}
                onOpenPort={openPort}
                onSetPortTarget={setPortTarget}
                onSetPortAllowedSources={setPortAllowedSources}
                onClosePort={closePort}
              />
            </NodeDetailSection>
            <NodeDetailSection title="Network">
              <NetworkModeSummary
                name={node.name || node.externalNodeId}
                memberType={resolveNetworkMemberType(node.networkMemberType)}
                onSetMode={
                  canChangeNetworkMode ? setNetworkMemberType : undefined
                }
              />
              <NetworkAddressCard
                localIp={node.connectivityInfo?.meshIpAddress}
                privateDns={node.connectivityInfo?.meshDnsName}
              />
            </NodeDetailSection>
            <NodeDetailSection
              id="Access"
              ref={sshAccessSectionRef}
              className="scroll-mt-6"
              title="Share SSH Access"
            >
              <NodeSSHAccessSection
                sshAccess={sshAccess}
                onGrant={grantSSHAccess}
                onRevoke={revokeSSHAccess}
              />
            </NodeDetailSection>
            <NodeDetailSection title="Using Brev CLI (SSH)">
              {!sshAccess?.length ? (
                <EnableSSHBanner />
              ) : (
                <CliCommands
                  name={node.name || node.externalNodeId}
                  resourceNoun="node"
                />
              )}
            </NodeDetailSection>
          </div>
        </main>
      </div>
      <RemoveNodeDialog
        open={removeNodeDialogOpen}
        onOpenChange={setRemoveNodeDialogOpen}
        nodeName={node.name || node.externalNodeId}
        onConfirm={async () => {
          await removeNode();
          history.push(`/org/${orgId}/environments`);
        }}
      />
    </>
  );
};

export default NodeDetail;
